import type { Bilingual, Crew, Formality, WorkerLang, Lang, ProductCategory, ProductIcon, ProductStatus, RoomIcon, TaskResult, Weekday } from './types';

/**
 * What the cloud service and the apps send each other.
 * Text an owner writes is stored as a LocalText: the original, plus the translation when there is one.
 */

export interface LocalText extends Bilingual {
  /** The language it was written in, or "catalog" for starter content written in both languages. */
  src: Lang | 'catalog';
  /** True while the other language is still waiting for automatic translation. */
  pending?: boolean;
  /** Which form ("tú" or "usted") the machine-made Spanish was written in, when the service allows choosing. */
  esFor?: Formality;
  /** The household's monthly allowance was used up; the other side stays empty until the owner fills it or next month. */
  overLimit?: boolean;
  /** The owner typed or corrected the translation by hand; the machine never overwrites it. */
  ownerEdited?: boolean;
  /** Re-translation after a "tú"/"usted" switch; does not count against the allowance. */
  free?: boolean;
  /**
   * The language is not confirmed yet: owners type in either language whatever their app's
   * language, so the translator detects it (2026-10-25: English typed in a Spanish app was filed
   * as Spanish and never translated).
   */
  detect?: boolean;
  /** The language was confirmed by the translator. */
  checked?: boolean;
}

export type Country = 'US' | 'MX';

export interface ApiOwner {
  id: string;
  name: string;
  email: string;
  lang: Lang;
  reportEmails: boolean;
}

export interface ApiHousehold {
  id: string;
  name: string;
  country: Country;
  timeZone: string;
  formality: Formality;
  workDays: Weekday[];
  workStart: string;
  workEnd: string;
  /** The garden's own work days and hours (no days: no garden yet). */
  garden: Schedule;
  translationsUsed: number;
  translationLimit: number;
}

/** Work days and hours of one crew. */
export interface Schedule {
  workDays: Weekday[];
  workStart: string;
  workEnd: string;
}

export interface ApiRoom {
  id: string;
  crew: Crew;
  name: LocalText;
  icon: RoomIcon;
  order: number;
}

export interface ApiTask {
  id: string;
  roomId: string;
  name: LocalText;
  minutes: number;
  instructions: LocalText | null;
  days: Weekday[];
  order: number;
}

export interface ApiProduct {
  id: string;
  crew: Crew;
  name: LocalText;
  category: ProductCategory;
  icon: ProductIcon;
  status: ProductStatus;
  statusBy: string | null;
  statusByName: string | null;
  statusAt: string | null;
  order: number;
}

export interface ApiStaff {
  id: string;
  crew: Crew;
  /** The language their page, instructions, and printed list are in. */
  lang: WorkerLang;
  firstName: string;
  linkActive: boolean;
  /** The private link, shown only to owners of the household. */
  link: string;
}

export interface ApiResult extends TaskResult {
  byName: string;
}

export interface Snapshot {
  owner: ApiOwner;
  household: ApiHousehold | null;
  owners: { id: string; name: string }[];
  rooms: ApiRoom[];
  tasks: ApiTask[];
  products: ApiProduct[];
  staff: ApiStaff[];
  today: { date: string; weekday: Weekday; results: Record<string, ApiResult> };
  /** Which tasks each crew does on each date, from yesterday to 13 days ahead: the weekly routine with any day moves applied. */
  plans: DayPlans;
  /** One-time day moves that still matter (from yesterday on). */
  moves: ApiDayMove[];
  /** Past daily reports, newest first (last 60 days). */
  reports: ReportListItem[];
  /** A second owner invited by email who has not signed in yet. */
  invitation: { email: string } | null;
}

/** Sent when an owner finishes first-run setup. */
export interface SetupInput {
  ownerName: string;
  lang: Lang;
  houseName: string;
  country: Country;
  timeZone: string;
  /** Ignored: every household uses "usted" (Mike, 2026-10-09). */
  formality?: Formality;
  workDays: Weekday[];
  workStart: string;
  workEnd: string;
  /** Starter rooms to create, each with the starter tasks the owner kept. */
  rooms: { catalogId: string; name: Bilingual; taskIndexes: number[] }[];
  staffFirstName?: string;
  /** The first worker's language (Spanish if not chosen). */
  staffLang?: WorkerLang;
  /** Seconds from opening setup to "Create my house" (decision 53), a number only. */
  setupSeconds?: number;
}

export const TRANSLATION_MONTHLY_LIMIT = 100;
export const TRANSLATION_FIRST_MONTH_EXTRA = 300;
export const MAX_TEXT_LENGTH = 500;

/** What the staff link shows: the house's rooms, tasks, and supplies, and today's checks. No owner details. */
/** Task ids per crew per date ("2026-10-27"). */
export type DayPlans = Record<string, Record<Crew, string[]>>;

/** "This Monday's unfinished tasks are done on Tuesday the 27th instead." */
export interface ApiDayMove {
  id: string;
  crew: Crew;
  fromDate: string;
  toDate: string;
  count: number;
}

export interface StaffView {
  staffId: string;
  staffFirstName: string;
  /** This person's crew: the page shows only its areas, supplies, and days. */
  crew: Crew;
  /** The language the page is shown in. */
  lang: WorkerLang;
  houseName: string;
  formality: Formality;
  today: { date: string; weekday: Weekday };
  /** This person's tasks for today and the next 6 days, with any day moves applied. */
  plans: Record<string, string[]>;
  rooms: ApiRoom[];
  tasks: ApiTask[];
  products: { id: string; name: LocalText; category: ProductCategory; icon: ProductIcon; status: ProductStatus; order: number }[];
  /** Today's checks by anyone in the house, so staff see what an owner already ticked. */
  results: Record<string, { status: 'done' | 'could_not'; at: string; mine: boolean; reason?: TaskResult['reason'] }>;
}

/** One tap on the staff link, saved on the phone first and sent when there is a connection. */
export type StaffAction =
  | { kind: 'check'; id: string; taskId: string; status: 'done' | 'could_not'; reason?: TaskResult['reason']; note?: string; at: string }
  | { kind: 'uncheck'; id: string; taskId: string; at: string }
  | { kind: 'product'; id: string; productId: string; status: ProductStatus; at: string };

/** One task's line in a daily report. A copy, so later edits never change past reports. */
export interface ReportLine {
  taskId: string;
  name: LocalText;
  roomName: LocalText;
  minutes: number;
  /** "not_reached" means nobody checked it by the household's end time (decision 20). */
  status: 'done' | 'could_not' | 'not_reached';
  at?: string;
  byName?: string;
  reason?: TaskResult['reason'];
  note?: LocalText;
}

export interface ReportSummary {
  date: string;
  /** Whose day this report covers (older reports, before the garden existed, have none: house). */
  crew?: Crew;
  total: number;
  done: number;
  couldNot: number;
  notReached: number;
  minutes: number;
  /** True once the day is over: staff finished everything, or the end time passed. */
  final: boolean;
  lines: ReportLine[];
  restock: { name: LocalText; status: ProductStatus }[];
}

export interface ReportListItem {
  date: string;
  crew: Crew;
  total: number;
  done: number;
  couldNot: number;
  notReached: number;
}
